import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { animate, m, useInView, useScroll, useSpring, useTransform } from "framer-motion";
import { usePrefersReducedMotion as useReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { cn } from "@/lib/utils";

/**
 * Scroll-linked motion for the home page, next to the one-shot reveals in
 * Reveal.tsx. Everything here collapses to a still page when the visitor asks
 * the OS for reduced motion, and only transform and opacity ever move.
 */

const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

/** The logo gradient, for the few places that draw it outside CSS. */
export const LOGO_GRADIENT = "linear-gradient(90deg, #086a53 0%, #36b588 100%)";

/** A thin bar in the logo gradient across the top, filling as the page is read. */
export function ScrollProgress() {
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 220, damping: 40, restDelta: 0.001 });
  if (reduced) return null;
  return (
    <m.div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-[2px] origin-left"
      style={{ scaleX, background: LOGO_GRADIENT }}
    />
  );
}

/** Moves its content against the scroll, `speed` px either way across the viewport. */
export function Parallax({ children, className, speed = 50, style }: { children: ReactNode; className?: string; speed?: number; style?: CSSProperties }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [speed, -speed]);
  return (
    <m.div ref={ref} className={className} style={{ ...style, y }}>
      {children}
    </m.div>
  );
}

/**
 * Tilted back and slightly small while it rises into view, flat by the time it
 * reaches the upper third. Used once, on the sample report in the hero.
 */
export function TiltIn({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start 0.25"] });
  const rotateX = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [16, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [0.92, 1]);
  return (
    <div className={className} style={{ perspective: 1600 }}>
      <m.div ref={ref} style={{ rotateX, scale, transformOrigin: "50% 0%" }}>
        {children}
      </m.div>
    </div>
  );
}

/**
 * A number that counts to its value the first time it is seen, and eases to
 * each new value after that. The text is written straight to the node, so the
 * server render and a visitor without script both get the final figure.
 */
export function CountUp({ value, format, className, style }: { value: number; format: (n: number) => string; className?: string; style?: CSSProperties }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -60px 0px" });
  const reduced = useReducedMotion();
  const shown = useRef<number | null>(null);
  const initial = useRef(format(value)).current;

  useEffect(() => {
    const el = ref.current;
    if (!el || !inView) return;
    if (reduced) {
      el.textContent = format(value);
      shown.current = value;
      return;
    }
    const first = shown.current === null;
    const controls = animate(shown.current ?? 0, value, {
      duration: first ? 1.4 : 0.5,
      ease: EASE,
      onUpdate: (v) => {
        el.textContent = format(v);
        shown.current = v;
      },
    });
    return () => controls.stop();
    // `format` is a new function each render; the value is what drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inView, reduced]);

  return (
    <span ref={ref} className={className} style={style}>
      {initial}
    </span>
  );
}

/** A hairline that draws itself left to right when it scrolls in, with a mint lead-in. */
export function DrawLine({ className }: { className?: string }) {
  const reduced = useReducedMotion();
  return (
    <m.span
      aria-hidden
      className={cn("pointer-events-none absolute left-0 top-0 block h-px w-full origin-left", className)}
      style={{ background: "linear-gradient(90deg, hsl(var(--ec-mint)) 0, hsl(var(--ec-mint)) 56px, hsl(var(--border)) 56px)" }}
      initial={reduced ? false : { scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, margin: "0px 0px -60px 0px" }}
      transition={{ duration: 1.1, ease: EASE }}
    />
  );
}

/** A bar that grows to `pct` of its track when it scrolls in. */
export function GrowBar({ pct, className, barClassName, delay = 0 }: { pct: number; className?: string; barClassName?: string; delay?: number }) {
  const reduced = useReducedMotion();
  return (
    <span className={cn("block h-2 overflow-hidden rounded-full bg-foreground/[0.06]", className)}>
      <m.span
        className={cn("block h-full origin-left rounded-full", barClassName)}
        style={{ width: `${pct}%` }}
        initial={reduced ? false : { scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, margin: "0px 0px -40px 0px" }}
        transition={{ duration: 1, ease: EASE, delay }}
      />
    </span>
  );
}

/**
 * A line that fills as its section scrolls past: across on wide screens,
 * down on phones. Draws the three steps of "How it works" together.
 */
export function ScrollLine({ className, vertical = false }: { className?: string; vertical?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.55"] });
  const fill = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [0, 1]);
  return (
    <div ref={ref} aria-hidden className={cn("pointer-events-none absolute overflow-hidden bg-[hsl(var(--ec-line-strong))]", className)}>
      <m.div
        className={cn("absolute inset-0", vertical ? "origin-top" : "origin-left")}
        style={{ [vertical ? "scaleY" : "scaleX"]: fill, background: vertical ? "linear-gradient(180deg, #086a53, #36b588)" : LOGO_GRADIENT }}
      />
    </div>
  );
}
